import {
  For,
  Show,
  onMount,
  createEffect,
  createMemo,
  createSignal,
} from "solid-js";
import autoAnimate from "@formkit/auto-animate";
import type { SuggestedQuestion } from "../types/suggestquestions";
import { PanelHeader } from "./PanelHeader";

interface Props {
  suggestions: SuggestedQuestion[];
  asked: string[];
  earlier?: SuggestedQuestion[];
  dismissed?: SuggestedQuestion[];
  onDismiss: (q: SuggestedQuestion) => void;
  onRestore?: (q: SuggestedQuestion) => void;
  onDismissEarlier?: (q: SuggestedQuestion) => void;
  onAsk?: (q: SuggestedQuestion) => void; // kept for optional fallback
  contactType: string;
  readOnly?: boolean;
  likelihood?: number | undefined;
  onReturnDrop?: (
    payload: {
      question: string;
      priority_level?: "Critical" | "Important" | "Informational" | "Screening";
      from?: string;
      id?: string;
    },
    toBucket?: "Critical" | "Important" | "Informational" | "Screening",
  ) => void;
}



const colours: Record<string, string> = {
  Critical: "bg-rose-50  border-rose-300  text-rose-900",
  Important: "bg-amber-50 border-amber-300 text-amber-900",
  Informational: "bg-indigo-50 border-indigo-300 text-indigo-900",
  Blockers: "bg-red-50   border-red-300   text-red-900",
  Progress: "bg-green-50 border-green-300 text-green-900",
  "Next Steps": "bg-blue-50  border-blue-300  text-blue-900",
};

function bucket(list: SuggestedQuestion[]) {
  return list.reduce<Record<string, SuggestedQuestion[]>>((acc, q) => {
    acc[q.priority_level] = [...(acc[q.priority_level] || []), q];
    return acc;
  }, {});
}
type Lvl = "Critical" | "Important" | "Informational" | "Screening";

export default function SuggestionsPanel(p: Props) {
  const [likelihood, setLikelihood] = createSignal<number | undefined>(undefined);
  createEffect(() => {
    setLikelihood(p.likelihood);
  });
  const [local, setLocal] = createSignal<SuggestedQuestion[]>(p.suggestions);
  const [openEarlier, setOpenEarlier] = createSignal<Record<Lvl, boolean>>({
    Critical: true,
    Important: true,
    Informational: true,
    Screening: true
  });

// Display different icons and titles for each of the table levels
const levelMetadataDictionary: Record<string, { icon: string, color: string, title: string, likelihood?: string }> = {
  Critical: { icon: "💡", color: "bg-rose-50  border-rose-300  text-rose-900" , title: "Critical Questions"},
  Important: { icon: "💡", color: "bg-amber-50 border-amber-300 text-amber-900" , title: "Important Questions"},
  Informational: { icon: "💡", color: "bg-indigo-50 border-indigo-300 text-indigo-900", title: "Informational Questions" },
  // Screening: { icon: "📄", color: "bg-gray-50 border-gray-300 text-gray-900", title: "Screening Results", likelihood: `Likelihood: ${likelihood() || 0}` },
};
  // refs per column (current and earlier) so auto-animate works
  const listRefs: Partial<Record<Lvl, HTMLUListElement | undefined>> = {};
  const earlierRefs: Partial<Record<Lvl, HTMLUListElement | undefined>> = {};

  createEffect(() => setLocal(p.suggestions));

  const grp = createMemo(() => bucket(local()));
  const earlierGrp = createMemo(() => bucket(p.earlier || []));
  const dismissedGrp = createMemo(() => bucket(p.dismissed || []));

  function normalize(s: string) {
    return (s || "").trim().toLowerCase();
  }

  function markDismissed(q: SuggestedQuestion) {
    // Remove from live suggestions immediately and persist dismissal
    setLocal((prev) =>
      prev.filter((it) => (it.id ?? it.question) !== (q.id ?? q.question)),
    );
    p.onDismiss(q);
  }
  function restore(q: SuggestedQuestion) {
    setLocal((prev) =>
      prev.map((it) => (it.id === q.id ? { ...it, dismissed: false } : it)),
    );
    p.onRestore?.(q);
  }

  const ordered = (lvl: Lvl) => {
    const items = grp()[lvl] || [];
    return [...items].sort(
      (a, b) => Number(!!a.dismissed) - Number(!!b.dismissed),
    );
  };
  const earlierOrdered = (lvl: Lvl) => {
    const items = (earlierGrp()[lvl] || []) as SuggestedQuestion[];
    return [...items].sort(
      (a, b) => Number(!!a.dismissed) - Number(!!b.dismissed),
    );
  };
  const dismissedOrdered = (lvl: Lvl) => {
    const items = (dismissedGrp()[lvl] || []) as SuggestedQuestion[];
    return items;
  };

  onMount(() => {
    (["Critical", "Important", "Informational"] as Lvl[]).forEach((lvl) => {
      if (listRefs[lvl]) autoAnimate(listRefs[lvl]!);
      if (earlierRefs[lvl]) autoAnimate(earlierRefs[lvl]!);
    });
  });

  function toggle(lvl: Lvl) {
    setOpenEarlier((s) => ({ ...s, [lvl]: !s[lvl] }));
  }

  const QuestionItem = (q: SuggestedQuestion, lvl: Lvl, from?: "earlier") => {
    const already = p.asked?.includes(q.question);
    const dismissed = !!q.dismissed;
    const canDrag = !dismissed && !already && !p.readOnly;

    return (
      <li
        draggable={canDrag}
        onDragStart={(e) => {
          try {
            const payload: any = {
              question: q.question,
              priority_level: q.priority_level,
              fieldKey: (q as any).fieldKey,
              id: q.id ?? undefined,
              origin: q.source, // template or context
            };
            if (from) payload.from = from; // only set when from="earlier"
            e.dataTransfer?.setData(
              "application/x-question",
              JSON.stringify(payload),
            );
          } catch {}
        }}
        class={`relative rounded border-l-2 px-2 py-3 pr-10 flex flex-col gap-0 w-full items-center text-center transition ${
          colours[lvl] || "bg-gray-50 border-gray-300 text-gray-900"
        } ${dismissed ? "opacity-60 line-through italic" : ""}`}
        title={
          from === "earlier" ? "Drag into Answered to mark as asked" : undefined
        }
      >
        {/* Removed per UX: item-level "Earlier" tag; column header already indicates Earlier section */}
        <div class="flex items-baseline gap-1">
          <span
            class={`text-[14px] leading-tight ${
              from === "earlier" ? "mt-3" : ""
            } line-clamp-2`}
          >
            {q.question}
          </span>
          <Show when={q.source}>
            <span class="text-[10px] px-1.5 py-0.5 rounded bg-white/70 border border-white/50 text-gray-600">
              {q.source}
            </span>
          </Show>
        </div>
        <Show when={!dismissed && !p.readOnly}>
          <button
            class="absolute top-1 right-1 text-gray-500 hover:text-gray-700 text-sm"
            title="Dismiss"
            onClick={() =>
              from === "earlier"
                ? (p.onDismiss(q), p.onDismissEarlier?.(q))
                : markDismissed(q)
            }
          >
            ×
          </button>
        </Show>
        <Show when={dismissed}>
          <button
            class="absolute top-1 right-1 text-gray-500 hover:text-gray-700 text-xs underline"
            title="Restore"
            onClick={() => (from === "earlier" ? p.onRestore?.(q) : restore(q))}
          >
            Restore
          </button>
        </Show>
      </li>
    );
  };

  const Column = (lvl: Lvl) => (
    <div class="card">
      <div class="flex items-baseline gap-2 mb-0.5">
       {levelMetadataDictionary[lvl]?.icon && <span >{levelMetadataDictionary[lvl].icon}</span>}
      
        <PanelHeader title={levelMetadataDictionary[lvl]?.title || `${lvl} Questions`} />
        <span class="text-[11px] text-gray-500 font-medium shrink-0 whitespace-nowrap ml-1">
          ({p.contactType})
        </span>
        {/* <Show when={lvl == "Screening"}>
        <span class="text-[11px] text-gray-500 font-medium shrink-0 whitespace-nowrap ml-1">
          Likelihood: {Math.round((likelihood()  || 0)*100)}%
        </span>
       </Show> */}
      </div>

      <Show
        when={ordered(lvl).length}
        fallback={
          <p class="text-gray-500 text-sm mt-1">
            No {lvl.toLowerCase()} questions yet.
          </p>
        }
      >
        <ul
          class="mt-1 space-y-1"
          ref={(el) => (listRefs[lvl] = el as HTMLUListElement)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            const data = e.dataTransfer?.getData("application/x-question");
            if (!data) return;
            try {
              const payload = JSON.parse(data || "{}");
              if (payload?.from && payload.from !== "answered") return;
              p.onReturnDrop?.(payload, lvl);
            } catch {}
          }}
        >
          <For each={ordered(lvl)}>{(q) => QuestionItem(q, lvl)}</For>
        </ul>
      </Show>

      <Show when={(earlierOrdered(lvl).length || 0) > 0}>
        <div class="mt-3 pt-2 border-t border-gray-200 flex items-center justify-between">
          <span class="text-xs font-semibold text-gray-600">
            Earlier suggestions
          </span>
          <button
            type="button"
            class="text-xs text-gray-600 hover:text-gray-800 underline"
            onClick={() => toggle(lvl)}
          >
            {openEarlier()[lvl] ? "Collapse" : "Expand"} (
            {earlierOrdered(lvl).length})
          </button>
        </div>

        <Show when={openEarlier()[lvl]}>
          <ul
            ref={(el) => (earlierRefs[lvl] = el as HTMLUListElement)}
            class="mt-2 space-y-1"
            onDragOver={(e) => e.preventDefault()}
          >
            <For each={earlierOrdered(lvl)}>
              {(q) => QuestionItem(q, lvl, "earlier")}
            </For>
          </ul>
        </Show>
      </Show>

      {/* Dismissed subsection under Earlier (by priority) */}
      <Show when={(dismissedOrdered(lvl).length || 0) > 0}>
        <div class="mt-3 pt-2 border-t border-gray-200">
          <span class="text-xs font-semibold text-gray-600">Dismissed</span>
          <ul class="mt-2 space-y-1">
            <For each={dismissedOrdered(lvl)}>
              {(q) => (
                <li
                  class={`relative rounded border-l-2 px-2 py-3 pr-10 flex flex-col gap-0 w-full items-center text-center opacity-60 italic ${
                    colours[lvl] || "bg-gray-50 border-gray-300 text-gray-900"
                  }`}
                >
                  <div class="flex items-baseline gap-1">
                    <span class="text-[14px] leading-tight line-clamp-2 line-through">
                      {q.question}
                    </span>
                    <Show when={q.source}>
                      <span class="text-[10px] px-1.5 py-0.5 rounded bg-white/70 border border-white/50 text-gray-600">
                        {q.source}
                      </span>
                    </Show>
                  </div>
                  <Show when={!p.readOnly}>
                    <button
                      class="absolute top-1 right-1 text-gray-500 hover:text-gray-700 text-xs underline"
                      title="Restore"
                      onClick={() => p.onRestore?.(q)}
                    >
                      Restore
                    </button>
                  </Show>
                </li>
              )}
            </For>
          </ul>
        </div>
      </Show>
    </div>
  );

  return (
    <>
      {Column("Critical")}
      {Column("Important")}
      {Column("Informational")}
      {/* {Column("Screening")} */}
    </>
  );
}
